import React, { forwardRef, useState } from 'react';
import clsx from 'clsx';
import passwordHiddenIcon from '@/assets/images/svg/password_hidden.svg';
import passwordVisibleIcon from '@/assets/images/svg/password_visible.svg';

/** @typedef AuthInputProps — Extends native input with label and error display. */
export interface AuthInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string | undefined;
}

/** Styled auth input with optional label, error message, and password visibility toggle. */
export const AuthInput = forwardRef<HTMLInputElement, AuthInputProps>(
  ({ label, error, className, id, type, ...props }, ref) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const isPasswordType = type === 'password';

    const togglePasswordVisibility = (e: React.MouseEvent) => {
      e.preventDefault();
      setIsPasswordVisible(!isPasswordVisible);
    };

    const labelClasses = clsx(
      'block font-sans font-medium text-[length:var(--vs-14,14px)] leading-21 mb-[var(--vs-8,8px)] ml-0.5',
      'text-white fold:text-brand-800'
    );

    const inputClasses = clsx(
      'h-[var(--vs-47,47px)] w-full rounded-10 px-4',
      isPasswordType && 'pr-[60px]',
      'bg-white border',
      error ? 'border-red-500' : 'border-gray-440',
      'font-jakarta font-normal text-[length:var(--vs-14,14px)] leading-160 tracking-[-0.02em]',
      'text-brand-800 placeholder:text-brand-800',
      'focus:outline-none focus:ring-0 focus:border-2 transition-all duration-200',
      'focus:border-accent-glow fold:focus:border-brand-750',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      className
    );

    const inputType = isPasswordType ? (isPasswordVisible ? 'text' : 'password') : type;

    return (
      <div className='w-full'>
        {label && (
          <label htmlFor={id} className={labelClasses}>
            {label}
          </label>
        )}

        <div className='relative'>
          <input ref={ref} id={id} type={inputType} className={inputClasses} {...props} />

          {isPasswordType && (
            <button
              type='button'
              onClick={togglePasswordVisibility}
              className='absolute right-[20px] top-1/2 -translate-y-1/2 p-1 transition-opacity hover:opacity-70'
              aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              <img
                src={isPasswordVisible ? passwordVisibleIcon : passwordHiddenIcon}
                alt=''
                className='size-5 select-none'
              />
            </button>
          )}
        </div>

        {error && <p className='animate-slide-in ml-1 mt-1 text-xs text-red-400'>{error}</p>}
      </div>
    );
  }
);

AuthInput.displayName = 'AuthInput';

export default AuthInput;
